class HatFitTabsV2 extends HTMLElement {
  constructor() {
    super();
    this.unsubscribers = [];
    this.mobileBreakpoint = 1024;
    this.videoObserver = null;
    this.videoVisibility = new Map();
    this.videoSyncFrame = null;
    this.activeDesktopVideo = null;
    this.activeMobileVideo = null;
    this.lastIsMobile = null;
    this.activeFullscreenVideo = null;
    this.activeFullscreenHostItem = null;
    this.inlinePlaybackBeforeFullscreen = null;
    this.userPausedInlineVideos = new WeakSet();
    this.videoProgressCache = new WeakMap();
    this.videoProgressRatios = new WeakMap();
    this.videoProgressActiveVideos = new Set();
    this.videoProgressLoopId = null;
    this.mobileControlTimers = new WeakMap();
    this.mobileControlsHideDelay = 800;
    this.mobileTapSuppressUntil = 0;
    this.playThreshold = 0.5;
    this.onDocumentFullscreenChange = this.onDocumentFullscreenChange.bind(this);
  }

  bind(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.unsubscribers.push(() => target.removeEventListener(type, handler, options));
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.tabs = this.querySelectorAll('[data-tab-target]');
    this.panels = this.querySelectorAll('[data-tab-panel]');
    this.fitItems = this.querySelectorAll('[data-fit-item]');
    this.switchTabButtons = this.querySelectorAll('[data-switch-tab]');
    this.inlineVideos = this.querySelectorAll('video[data-video-role="inline"]');
    this.fullscreenVideos = this.querySelectorAll('video[data-video-role="fullscreen"]');

    this.tabs.forEach((tab) => {
      this.bind(tab, 'click', () => {
        this.activateTab(tab.getAttribute('data-tab-target'));
      });
    });

    this.switchTabButtons.forEach((button) => {
      this.bind(button, 'click', () => {
        this.activateTab(button.getAttribute('data-switch-tab'));
      });
    });

    this.bind(this, 'click', (event) => {
      const openTrigger = event.target.closest('[data-fit-popup-open]');
      if (openTrigger && this.contains(openTrigger)) {
        const key = openTrigger.getAttribute('data-fit-popup-open');
        const type = openTrigger.getAttribute('data-fit-popup-type');
        if (key && type) this.openFitPopup(key, type);
        return;
      }

      const closeTrigger = event.target.closest('[data-fit-popup-close]');
      if (closeTrigger && this.contains(closeTrigger)) {
        const key = closeTrigger.getAttribute('data-fit-popup-close');
        if (key) this.closeFitPopup(key);
      }
    });

    this.bind(document, 'fullscreenchange', this.onDocumentFullscreenChange);
    this.bind(document, 'webkitfullscreenchange', this.onDocumentFullscreenChange);

    this.fitItems.forEach((item) => {
      const toggle = item.querySelector('[data-fit-toggle]');
      const content = item.querySelector('[data-fit-content]');
      const { inline: videoInline, fullscreen: videoFs } = this.getFitVideos(item);

      if (!toggle || !content) return;

      const toggleHandler = () => {
        if (!this.isMobileOrTablet()) return;
        const isOpen = item.classList.contains('is-open');
        this.setItemState(item, !isOpen, false);
      };

      toggle.addEventListener('click', toggleHandler);
      this.unsubscribers.push(() => toggle.removeEventListener('click', toggleHandler));

      const videoPlayButton = item.querySelector('[data-video-play]');
      const videoPauseButton = item.querySelector('[data-video-pause]');

      const fullscreenTrigger = item.querySelector('[data-video-fullscreen-trigger]');
      const videoTouchOverlay = item.querySelector('[data-video-touch]');

      if (videoTouchOverlay && videoInline) {
        const videoTapHandler = (event) => {
          if (Date.now() < this.mobileTapSuppressUntil) return;
          event.preventDefault();
          this.toggleMobileVideoFromTap(videoInline);
        };

        videoTouchOverlay.addEventListener('click', videoTapHandler);
        this.unsubscribers.push(() =>
          videoTouchOverlay.removeEventListener('click', videoTapHandler),
        );
      }

      if (fullscreenTrigger) {
        const fullscreenHandler = (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!this.isMobileOrTablet()) return;
          this.enterFitVideoFullscreen(item);
        };

        fullscreenTrigger.addEventListener('click', fullscreenHandler);
        this.unsubscribers.push(() => fullscreenTrigger.removeEventListener('click', fullscreenHandler));
      }

      if (videoPlayButton && videoInline) {
        const playHandler = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.playInlineVideo(videoInline);
          // Playing: flash controls, then auto-fade after delay.
          this.showMobileVideoControls(videoInline);
        };

        videoPlayButton.addEventListener('click', playHandler);
        this.unsubscribers.push(() => videoPlayButton.removeEventListener('click', playHandler));
      }

      if (videoInline && videoFs) {
        const inlinePlayGuard = () => {
          if (
            this.isFullscreenSourceActive() &&
            this.activeFullscreenHostItem === item &&
            !videoInline.paused
          ) {
            videoInline.pause();
          }
        };

        videoInline.addEventListener('play', inlinePlayGuard);
        this.unsubscribers.push(() => videoInline.removeEventListener('play', inlinePlayGuard));
      }

      if (videoPauseButton && videoInline) {
        const pauseHandler = (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.pauseInlineVideo(videoInline, { fromUser: true, exitFullscreen: true });
          // Paused: keep play button sticky until play or tap-away.
          this.revealMobileVideoControls(videoInline);
        };

        videoPauseButton.addEventListener('click', pauseHandler);
        this.unsubscribers.push(() => videoPauseButton.removeEventListener('click', pauseHandler));
      }

      const webkitEndFs = () => {
        if (!this.activeFullscreenVideo) return;
        this.clearNativeFullscreenPrep(item);
        this.exitFitVideoFullscreen(item);
      };

      [videoInline, videoFs].filter(Boolean).forEach((v) => {
        v.addEventListener('webkitendfullscreen', webkitEndFs);
        this.unsubscribers.push(() => v.removeEventListener('webkitendfullscreen', webkitEndFs));
      });
    });

    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleViewportChange();
    this.bind(window, 'resize', this.handleViewportChange);

    this.configureVideoPreload();
    this.setupVideoObserver();
    this.setupVideoProgress();
  }

  disconnectedCallback() {
    if (this.videoProgressLoopId) {
      cancelAnimationFrame(this.videoProgressLoopId);
      this.videoProgressLoopId = null;
    }
    this.videoProgressActiveVideos.clear();
    this.clearAllMobileControlTimers();
    this.unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [];
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }
    if (this.videoSyncFrame) {
      cancelAnimationFrame(this.videoSyncFrame);
      this.videoSyncFrame = null;
    }
    this.videoVisibility.clear();
    this.activeDesktopVideo = null;
    this.activeMobileVideo = null;
    this.activeFullscreenHostItem = null;
    this.inlinePlaybackBeforeFullscreen = null;
    this.dataset.initialized = 'false';
  }

  isMobileOrTablet() {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  openFitPopup(modalKey, type) {
    const modal = this.querySelector('[data-fit-popup-modal="' + modalKey + '"]');
    if (!modal) return;

    modal.querySelectorAll('[data-fit-popup-image]').forEach((panel) => {
      panel.classList.toggle('hidden', panel.getAttribute('data-fit-popup-image') !== type);
    });

    modal.classList.remove('hidden');
    document.documentElement.classList.add('overflow-hidden');
  }

  closeFitPopup(modalKey) {
    const modal = this.querySelector('[data-fit-popup-modal="' + modalKey + '"]');
    if (!modal) return;

    modal.classList.add('hidden');
    document.documentElement.classList.remove('overflow-hidden');
  }

  getFitVideos(item) {
    const inline = item.querySelector('video[data-video-role="inline"]');
    const fullscreen = item.querySelector('video[data-video-role="fullscreen"]');
    return { inline, fullscreen };
  }

  playInlineVideo(videoInline) {
    if (!videoInline) return;

    videoInline.controls = false;
    this.userPausedInlineVideos.delete(videoInline);

    if (this.isMobileOrTablet()) {
      this.pauseAllMobileInlineVideos(videoInline);
    } else {
      this.pauseAllDesktopInlineVideos(videoInline);
    }

    videoInline.play().catch(() => {});
    this.setVideoButtonState(videoInline, true);
  }

  pauseInlineVideo(videoInline, { fromUser = false, exitFullscreen = false } = {}) {
    if (!videoInline) return;

    const media = videoInline.closest('.hat-fit-tabs-v2__fit-media');
    const videoFs = media?.querySelector('video[data-video-role="fullscreen"]');

    if (exitFullscreen) {
      const fsEl = this.getFullscreenElement();
      if (fsEl && (fsEl === videoInline || fsEl === videoFs)) {
        this.exitDocumentFullscreen();
      }
      this.activeFullscreenVideo = null;
      this.activeFullscreenHostItem = null;
    }

    if (videoFs) {
      videoFs.pause();
      videoFs.controls = false;
    }

    videoInline.pause();
    videoInline.controls = false;

    if (fromUser) {
      this.userPausedInlineVideos.add(videoInline);
    }

    if (this.activeMobileVideo === videoInline) {
      this.activeMobileVideo = null;
    }

    if (this.activeDesktopVideo === videoInline) {
      this.activeDesktopVideo = null;
    }

    this.setVideoButtonState(videoInline, false);
  }

  toggleMobileVideoFromTap(videoInline) {
    if (!videoInline) return;

    // Tap/click anywhere on the video toggles playback.
    if (videoInline.paused) {
      this.playInlineVideo(videoInline);
      this.showMobileVideoControls(videoInline);
      return;
    }

    // Paused via tap: keep play button sticky until play or another tap.
    this.pauseInlineVideo(videoInline, { fromUser: true });
    this.revealMobileVideoControls(videoInline);
  }

  prepareFullscreenVideo(item) {
    const { inline: videoInline, fullscreen: videoFs } = this.getFitVideos(item);
    if (!videoInline || !videoFs) return;

    videoFs.preload = 'auto';
    if (videoFs.readyState < 1) {
      videoFs.load();
    }

    if (videoInline.currentTime > 0) {
      videoFs.currentTime = videoInline.currentTime;
    }

    this.pauseInlineForFullscreenHost(item);
    this.setNativeFullscreenPrep(item, videoFs, true);
  }

  getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  exitDocumentFullscreen() {
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    } else if (document.webkitExitFullscreen) {
      document.webkitExitFullscreen();
    }
  }

  isVideoInFullscreen(video) {
    return this.getFullscreenElement() === video;
  }

  isFitFullscreenSessionActive() {
    return Boolean(this.activeFullscreenVideo);
  }

  isFullscreenSourceActive() {
    return this.activeFullscreenVideo?.getAttribute('data-video-role') === 'fullscreen';
  }

  pauseInlineForFullscreenHost(hostItem) {
    if (!hostItem) return;

    const { inline: videoInline } = this.getFitVideos(hostItem);
    if (!videoInline || videoInline.paused) return;

    videoInline.pause();
    if (this.activeMobileVideo === videoInline) {
      this.activeMobileVideo = null;
    }
  }

  pauseHiddenFullscreenVideos() {
    const fsEl = this.getFullscreenElement();

    this.fullscreenVideos.forEach((video) => {
      if (video !== fsEl && !video.paused) {
        video.pause();
      }
    });
  }

  setNativeFullscreenPrep(hostItem, target, isActive) {
    const media = hostItem?.querySelector('.hat-fit-tabs-v2__fit-media');
    if (!media || target?.getAttribute('data-video-role') !== 'fullscreen') return;

    media.classList.toggle('is-native-fullscreen-prep', Boolean(isActive));
  }

  clearNativeFullscreenPrep(hostItem) {
    const media = hostItem?.querySelector('.hat-fit-tabs-v2__fit-media');
    media?.classList.remove('is-native-fullscreen-prep');
  }

  playFullscreenTarget(target, videoInline, allowWithoutDomFullscreen) {
    if (!target) return;

    const isFullscreenSource = target.getAttribute('data-video-role') === 'fullscreen';
    if (isFullscreenSource && !allowWithoutDomFullscreen && !this.isVideoInFullscreen(target)) {
      return;
    }

    if (isFullscreenSource && videoInline) {
      this.pauseInlineForFullscreenHost(this.activeFullscreenHostItem || videoInline.closest('[data-fit-item]'));
    }

    target.play().catch(() => {});

    if (videoInline) {
      this.setVideoButtonState(videoInline, isFullscreenSource ? false : !target.paused);
    }
  }

  requestFitVideoFullscreen(target, videoInline, videoFs, hostItem) {
    const isFullscreenSource = target.getAttribute('data-video-role') === 'fullscreen';
    const onEntered = () => this.playFullscreenTarget(target, videoInline, false);

    const onFullscreenUnavailable = () => {
      this.clearNativeFullscreenPrep(hostItem);
      this.activeFullscreenVideo = null;
      this.activeFullscreenHostItem = null;

      if (isFullscreenSource) {
        if (videoFs) {
          videoFs.pause();
        }
        this.resumeInlineVideoAfterFullscreen(hostItem);
        return;
      }

      onEntered();
    };

    const tryWebkitNative = (webkitTarget) => {
      if (!webkitTarget?.webkitEnterFullscreen) {
        onFullscreenUnavailable();
        return;
      }

      this.activeFullscreenVideo = webkitTarget;
      this.setNativeFullscreenPrep(hostItem, webkitTarget, true);
      this.enterWebkitVideoFullscreen(
        webkitTarget,
        videoInline,
        videoFs,
        hostItem,
        () => {},
        () => {
          this.clearNativeFullscreenPrep(hostItem);

          if (webkitTarget !== videoInline && videoInline?.webkitEnterFullscreen) {
            this.activeFullscreenVideo = videoInline;
            tryWebkitNative(videoInline);
            return;
          }

          onFullscreenUnavailable();
        },
      );
    };

    // iOS Safari requires webkitEnterFullscreen synchronously inside the user gesture.
    if (this.isMobileOrTablet() && target.webkitEnterFullscreen) {
      tryWebkitNative(target);
      return;
    }

    if (isFullscreenSource) {
      this.setNativeFullscreenPrep(hostItem, target, true);
    }

    if (target.requestFullscreen) {
      target
        .requestFullscreen()
        .then(onEntered)
        .catch(() => {
          if (target.webkitEnterFullscreen) {
            tryWebkitNative(target);
            return;
          }

          this.clearNativeFullscreenPrep(hostItem);
          onFullscreenUnavailable();
        });
      return;
    }

    if (target.webkitEnterFullscreen) {
      tryWebkitNative(target);
      return;
    }

    onFullscreenUnavailable();
  }

  enterWebkitVideoFullscreen(target, videoInline, videoFs, hostItem, _onEntered, onFullscreenUnavailable) {
    if (!target.webkitEnterFullscreen) {
      onFullscreenUnavailable();
      return;
    }

    const onBegin = () => {
      target.removeEventListener('webkitbeginfullscreen', onBegin);
      this.clearNativeFullscreenPrep(hostItem);
      this.playFullscreenTarget(target, videoInline, true);
    };

    target.addEventListener('webkitbeginfullscreen', onBegin);
    this.unsubscribers.push(() => target.removeEventListener('webkitbeginfullscreen', onBegin));

    try {
      target.webkitEnterFullscreen();
    } catch (e) {
      target.removeEventListener('webkitbeginfullscreen', onBegin);
      onFullscreenUnavailable();
    }
  }

  resetFitVideo(item) {
    const { inline: videoInline, fullscreen: videoFs } = this.getFitVideos(item);
    const videos = [videoInline, videoFs].filter(Boolean);
    if (!videos.length) return;

    const fsEl = this.getFullscreenElement();
    if (fsEl && videos.includes(fsEl)) {
      this.exitDocumentFullscreen();
    }

    if (this.activeFullscreenVideo && videos.includes(this.activeFullscreenVideo)) {
      this.activeFullscreenVideo = null;
    }

    videos.forEach((video) => {
      video.pause();
      video.currentTime = 0;
      video.controls = false;
    });

    if (videoInline) {
      this.stopVideoProgressLoop(videoInline);
      this.hideMobileVideoControls(videoInline);
      this.userPausedInlineVideos.delete(videoInline);
      if (this.activeMobileVideo === videoInline) {
        this.activeMobileVideo = null;
      }
      this.resetVideoProgress(videoInline);
      this.setVideoButtonState(videoInline, false);
    }

    if (videoFs) {
      videoFs.pause();
      videoFs.controls = false;
    }

    this.clearNativeFullscreenPrep(item);
  }

  enterFitVideoFullscreen(item) {
    const { inline: videoInline, fullscreen: videoFs } = this.getFitVideos(item);
    if (!videoInline) return;

    if (!this.isMobileOrTablet()) {
      this.activeFullscreenHostItem = item;
      this.pauseHiddenFullscreenVideos();
      this.activeFullscreenVideo = videoInline;
      videoInline.controls = false;
      videoInline.muted = true;
      videoInline.pause();
      videoInline.play().catch(() => {});
      this.setVideoButtonState(videoInline, true);
      return;
    }

    const target = videoFs || videoInline;

    this.inlinePlaybackBeforeFullscreen = {
      currentTime: videoInline.currentTime,
    };
    this.activeFullscreenHostItem = item;
    this.activeFullscreenVideo = target;

    if (videoFs) {
      this.prepareFullscreenVideo(item);
    } else {
      this.pauseInlineForFullscreenHost(item);
    }

    target.muted = true;
    target.controls = true;

    if (target.paused) {
      target.play().catch(() => {});
    }

    try {
      if (target.webkitEnterFullscreen) {
        const onBegin = () => {
          target.removeEventListener('webkitbeginfullscreen', onBegin);
          this.clearNativeFullscreenPrep(item);
        };
        target.addEventListener('webkitbeginfullscreen', onBegin);
        this.unsubscribers.push(() => target.removeEventListener('webkitbeginfullscreen', onBegin));
        target.webkitEnterFullscreen();
        return;
      }

      if (target.requestFullscreen) {
        target
          .requestFullscreen()
          .then(() => {
            target.play().catch(() => {});
          })
          .catch(() => {
            this.exitFitVideoFullscreen(item);
          });
      }
    } catch (error) {
      this.exitFitVideoFullscreen(item);
    }
  }

  onDocumentFullscreenChange() {
    const fsEl = this.getFullscreenElement();

    if (fsEl && fsEl === this.activeFullscreenVideo) {
      const hostItem = fsEl.closest('[data-fit-item]');
      const videoInline = hostItem ? this.getFitVideos(hostItem).inline : null;
      this.playFullscreenTarget(fsEl, videoInline, false);
      return;
    }

    if (fsEl) return;
    if (!this.activeFullscreenVideo) return;
    const hostItem = this.activeFullscreenHostItem || this.activeFullscreenVideo.closest('[data-fit-item]');
    this.exitFitVideoFullscreen(hostItem);
  }

  exitFitVideoFullscreen(hostItem) {
    const target = this.activeFullscreenVideo;
    this.activeFullscreenVideo = null;
    this.activeFullscreenHostItem = null;
    this.clearNativeFullscreenPrep(hostItem);

    if (target) {
      target.pause();
      target.controls = false;
    }

    if (!hostItem || !this.isMobileOrTablet()) {
      this.inlinePlaybackBeforeFullscreen = null;
      return;
    }

    this.resumeInlineVideoAfterFullscreen(hostItem);
  }

  resumeInlineVideoAfterFullscreen(hostItem) {
    const { inline: videoInline, fullscreen: videoFs } = this.getFitVideos(hostItem);
    if (!videoInline) return;

    const content = hostItem.querySelector('[data-fit-content]');
    const isAccordionOpen =
      hostItem.classList.contains('is-open') && !content?.classList.contains('hidden');

    if (videoFs) {
      if (videoFs.currentTime > 0) {
        videoInline.currentTime = videoFs.currentTime;
      }
      videoFs.pause();
      videoFs.controls = false;
    } else if (this.inlinePlaybackBeforeFullscreen?.currentTime != null) {
      videoInline.currentTime = this.inlinePlaybackBeforeFullscreen.currentTime;
    }

    this.inlinePlaybackBeforeFullscreen = null;
    videoInline.controls = false;

    if (!isAccordionOpen || this.userPausedInlineVideos.has(videoInline)) {
      this.setVideoButtonState(videoInline, false);
      return;
    }

    if (this.getVideoVisibilityRatio(videoInline) >= this.playThreshold) {
      videoInline.play().catch(() => {});
      this.activeMobileVideo = videoInline;
      this.setVideoButtonState(videoInline, true);
    } else {
      videoInline.pause();
      this.setVideoButtonState(videoInline, false);
      this.scheduleVideoSync();
    }
  }

  handleViewportChange() {
    const isNarrowViewport = this.isMobileOrTablet();

    if (this.lastIsMobile === isNarrowViewport) {
      return;
    }

    this.lastIsMobile = isNarrowViewport;
    this.configureVideoPreload();

    if (!isNarrowViewport) {
      this.clearAllMobileControlTimers();
      this.querySelectorAll('.hat-fit-tabs-v2__fit-media.is-controls-visible').forEach((media) => {
        media.classList.remove('is-controls-visible');
      });
    }

    if (isNarrowViewport) {
      this.pauseAllDesktopInlineVideos();
      this.pauseAllMobileInlineVideos();
      this.activeDesktopVideo = null;
      this.fitItems.forEach((item) => {
        this.setItemState(item, false, false);
      });
    } else {
      this.fitItems.forEach((item) => {
        this.setItemState(item, true, false);
      });
      this.scheduleVideoSync();
    }
  }

  setItemState(item, open, closeOthers) {
    if (closeOthers && open) {
      this.fitItems.forEach((other) => {
        if (other !== item) this.setItemState(other, false, false);
      });
    }

    const toggle = item.querySelector('[data-fit-toggle]');
    const content = item.querySelector('[data-fit-content]');
    const { inline: videoInline } = this.getFitVideos(item);
    if (!toggle || !content) return;

    item.classList.toggle('is-open', open);
    content.classList.toggle('hidden', !open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (videoInline) {
      if (open && this.isMobileOrTablet()) {
        this.userPausedInlineVideos.delete(videoInline);
        // Ignore the ghost click that can land on the video after accordion expand.
        this.mobileTapSuppressUntil = Date.now() + 600;
        this.hideMobileVideoControls(videoInline);
        this.scheduleVideoSync();
      } else if (!open) {
        if (this.activeMobileVideo === videoInline) {
          this.activeMobileVideo = null;
        }
        this.resetFitVideo(item);
      }
    }
  }

  configureVideoPreload() {
    this.inlineVideos.forEach((video) => {
      video.preload = 'metadata';
    });

    this.fullscreenVideos.forEach((video) => {
      video.preload = 'none';
      video.pause();
    });
  }

  isVideoInActivePanel(video) {
    const panel = video.closest('[data-tab-panel]');
    return !panel || !panel.hidden;
  }

  isMobileInlineVideoEligible(video) {
    if (video.getAttribute('data-video-role') === 'fullscreen') return false;
    if (this.isFullscreenSourceActive()) return false;
    if (!this.isVideoInActivePanel(video)) return false;

    const item = video.closest('[data-fit-item]');
    if (!item) return false;

    const content = item.querySelector('[data-fit-content]');
    if (!item.classList.contains('is-open') || content?.classList.contains('hidden')) {
      return false;
    }

    return !this.userPausedInlineVideos.has(video);
  }

  getVideoVisibilityRatio(video) {
    return this.videoVisibility.get(video) ?? 0;
  }

  pauseOtherInlineVideos(exceptVideo) {
    this.inlineVideos.forEach((video) => {
      if (video === exceptVideo || video.paused) return;
      video.pause();
      this.setVideoButtonState(video, false);
    });
  }

  pauseAllDesktopInlineVideos(exceptVideo) {
    this.pauseOtherInlineVideos(exceptVideo);
    this.activeDesktopVideo = exceptVideo || null;
  }

  prepareDesktopVideoPlayback(video) {
    if (video.preload !== 'auto') {
      video.preload = 'auto';
    }
  }

  playDesktopVideo(video) {
    if (!video || this.isMobileOrTablet()) return;
    if (this.userPausedInlineVideos.has(video)) return;

    this.prepareDesktopVideoPlayback(video);
    this.pauseAllDesktopInlineVideos(video);
    this.activeDesktopVideo = video;

    video.play().catch(() => {});
    this.setVideoButtonState(video, true);
    this.hideMobileVideoControls(video);
  }

  scheduleVideoSync() {
    if (this.videoSyncFrame) return;

    this.videoSyncFrame = requestAnimationFrame(() => {
      this.videoSyncFrame = null;
      if (this.isMobileOrTablet()) {
        this.syncMobileVideoPlayback();
      } else {
        this.syncDesktopVideoPlayback();
      }
    });
  }

  pauseAllMobileInlineVideos(exceptVideo) {
    this.pauseOtherInlineVideos(exceptVideo);
    this.activeMobileVideo = exceptVideo || null;
  }

  playMobileVideo(video) {
    if (!video || !this.isMobileOrTablet()) return;
    if (this.isFitFullscreenSessionActive()) return;

    this.pauseAllMobileInlineVideos(video);
    this.activeMobileVideo = video;
    video.play().catch(() => {});
    this.setVideoButtonState(video, true);
    this.hideMobileVideoControls(video);
  }

  findBestVisibleVideo(isEligible) {
    let bestVideo = null;
    let bestRatio = 0;

    this.videoVisibility.forEach((ratio, video) => {
      if (!isEligible(video)) return;
      if (ratio >= this.playThreshold && ratio > bestRatio) {
        bestRatio = ratio;
        bestVideo = video;
      }
    });

    return bestVideo;
  }

  syncMobileVideoPlayback() {
    if (!this.isMobileOrTablet()) return;

    this.pauseHiddenFullscreenVideos();

    if (this.isFitFullscreenSessionActive()) {
      if (this.isFullscreenSourceActive()) {
        this.pauseInlineForFullscreenHost(this.activeFullscreenHostItem);
      }
      return;
    }

    const bestVideo = this.findBestVisibleVideo((video) => this.isMobileInlineVideoEligible(video));

    if (!bestVideo) {
      this.pauseAllMobileInlineVideos();
      return;
    }

    if (this.activeMobileVideo === bestVideo && !bestVideo.paused) {
      return;
    }

    this.playMobileVideo(bestVideo);
  }

  syncDesktopVideoPlayback() {
    if (this.isMobileOrTablet()) return;

    this.pauseHiddenFullscreenVideos();

    const bestVideo = this.findBestVisibleVideo(
      (video) => this.isVideoInActivePanel(video) && !this.userPausedInlineVideos.has(video),
    );

    if (!bestVideo) {
      this.pauseAllDesktopInlineVideos();
      return;
    }

    if (this.activeDesktopVideo === bestVideo && !bestVideo.paused) {
      return;
    }

    this.playDesktopVideo(bestVideo);
  }

  setupVideoObserver() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (!(video instanceof HTMLVideoElement)) return;
          this.videoVisibility.set(video, entry.intersectionRatio);
        });

        this.scheduleVideoSync();
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    );

    this.inlineVideos.forEach((video) => {
      this.videoObserver.observe(video);
      this.videoVisibility.set(video, 0);
    });
  }

  activateTab(target) {
    if (!target) return;

    const activeTab = this.querySelector('[data-tab-target].is-active');
    const isSameTab = activeTab?.getAttribute('data-tab-target') === target;

    this.pauseVideosInHiddenPanels();

    this.tabs.forEach((item) => {
      const active = item.getAttribute('data-tab-target') === target;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.panels.forEach((panel) => {
      const active = panel.getAttribute('data-tab-panel') === target;
      panel.classList.toggle('is-active', active);
      panel.hidden = !active;
    });

    if (!isSameTab) {
      this.scrollToTabsTop();
    }

    this.scheduleVideoSync();
  }

  scrollToTabsTop() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }

  pauseVideosInHiddenPanels() {
    this.panels.forEach((panel) => {
      if (!panel.hidden) return;

      panel.querySelectorAll('video[data-video-role="inline"]').forEach((video) => {
        if (video.paused) return;
        video.pause();
        this.setVideoButtonState(video, false);
        if (this.activeDesktopVideo === video) {
          this.activeDesktopVideo = null;
        }
        if (this.activeMobileVideo === video) {
          this.activeMobileVideo = null;
        }
      });

      panel.querySelectorAll('video[data-video-role="fullscreen"]').forEach((video) => {
        if (video.paused) return;
        video.pause();
      });
    });
  }

  setVideoButtonState(video, isPlaying) {
    const media = video.closest('.hat-fit-tabs-v2__fit-media');
    if (!media) return;
    const playButton = media.querySelector('[data-video-play]');
    const pauseButton = media.querySelector('[data-video-pause]');
    if (playButton) playButton.classList.toggle('active', !isPlaying);
    if (pauseButton) pauseButton.classList.toggle('active', isPlaying);
    media.classList.toggle('is-video-playing', isPlaying);
    this.setVideoProgressVisible(video, isPlaying || video.currentTime > 0);
  }

  clearMobileControlsHide(video) {
    const timeoutId = this.mobileControlTimers.get(video);
    if (!timeoutId) return;

    clearTimeout(timeoutId);
    this.mobileControlTimers.delete(video);
  }

  clearAllMobileControlTimers() {
    this.inlineVideos.forEach((video) => {
      this.clearMobileControlsHide(video);
    });
  }

  revealMobileVideoControls(video) {
    if (!video) return;

    const media = video.closest('.hat-fit-tabs-v2__fit-media');
    if (!media) return;

    this.clearMobileControlsHide(video);
    media.classList.add('is-controls-visible');
  }

  showMobileVideoControls(video) {
    this.revealMobileVideoControls(video);
    this.scheduleMobileControlsHide(video);
  }

  hideMobileVideoControls(video) {
    if (!video) return;

    this.clearMobileControlsHide(video);

    const media = video.closest('.hat-fit-tabs-v2__fit-media');
    if (media) {
      media.classList.remove('is-controls-visible');
    }
  }

  scheduleMobileControlsHide(video) {
    this.clearMobileControlsHide(video);

    // Play button stays sticky while paused — only auto-fade during playback.
    if (!video || video.paused) return;

    const timeoutId = window.setTimeout(() => {
      this.mobileControlTimers.delete(video);
      if (video.paused) return;
      this.hideMobileVideoControls(video);
    }, this.mobileControlsHideDelay);

    this.mobileControlTimers.set(video, timeoutId);
  }

  scheduleMobileControlsHideAfterInteraction(video) {
    this.scheduleMobileControlsHide(video);
  }

  getVideoProgressRatio(video, time = video.currentTime) {
    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) return 0;
    return Math.min(1, Math.max(0, time / duration));
  }

  setVideoProgressFill(video, ratio) {
    if (this.videoProgressRatios.get(video) === ratio) return;

    this.videoProgressRatios.set(video, ratio);

    const progress = this.getVideoProgressElements(video);
    if (!progress) return;

    if (progress.fill) {
      progress.fill.style.transform = `scaleX(${ratio})`;
    }

    if (progress.track) {
      progress.track.style.setProperty('--progress', String(ratio));
    }
  }

  seekVideoProgress(video, ratio) {
    if (!video) return;

    const clampedRatio = Math.min(1, Math.max(0, ratio));
    const duration = video.duration;

    if (Number.isFinite(duration) && duration > 0) {
      video.currentTime = clampedRatio * duration;
    }

    this.setVideoProgressFill(video, clampedRatio);
    this.setVideoProgressVisible(video, clampedRatio > 0 || !video.paused);
  }

  setupVideoProgressScrub(video) {
    const progress = this.getVideoProgressElements(video);
    if (!progress?.track) return;

    const track = progress.track;
    let wasPlaying = false;
    let dragRect = null;

    const getRatio = (clientX) => {
      const rect = dragRect || track.getBoundingClientRect();
      if (!rect.width) return 0;
      return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    };

    const endScrub = (event) => {
      track.classList.remove('is-dragging');

      if (event?.pointerId != null && track.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }

      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', endScrub);
      track.removeEventListener('pointercancel', endScrub);
      dragRect = null;

      if (wasPlaying) {
        video.play().catch(() => {});
        this.setVideoButtonState(video, true);
      } else {
        this.setVideoButtonState(video, false);
      }

      this.scheduleMobileControlsHideAfterInteraction(video);
    };

    const onPointerMove = (event) => {
      event.preventDefault();
      this.seekVideoProgress(video, getRatio(event.clientX));
    };

    const onPointerDown = (event) => {
      if (event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      dragRect = track.getBoundingClientRect();
      wasPlaying = !video.paused;
      track.classList.add('is-dragging');
      this.revealMobileVideoControls(video);

      if (wasPlaying) {
        video.pause();
      }

      this.seekVideoProgress(video, getRatio(event.clientX));

      track.setPointerCapture(event.pointerId);
      track.addEventListener('pointermove', onPointerMove);
      track.addEventListener('pointerup', endScrub);
      track.addEventListener('pointercancel', endScrub);
    };

    track.addEventListener('pointerdown', onPointerDown);
    this.unsubscribers.push(() => track.removeEventListener('pointerdown', onPointerDown));
  }

  getVideoProgressElements(video) {
    if (!video) return null;

    const cached = this.videoProgressCache.get(video);
    if (cached) return cached;

    const media = video.closest('.hat-fit-tabs-v2__fit-media');
    if (!media) return null;

    const elements = {
      track: media.querySelector('[data-video-progress]'),
      fill: media.querySelector('[data-video-progress-fill]'),
      thumb: media.querySelector('[data-video-progress-thumb]'),
    };
    this.videoProgressCache.set(video, elements);
    return elements;
  }

  updateVideoProgress(video) {
    this.setVideoProgressFill(video, this.getVideoProgressRatio(video));
  }

  scheduleVideoProgressLoop() {
    if (this.videoProgressLoopId) return;

    const tick = () => {
      if (!this.videoProgressActiveVideos.size) {
        this.videoProgressLoopId = null;
        return;
      }

      this.videoProgressActiveVideos.forEach((activeVideo) => {
        if (activeVideo.paused || activeVideo.ended) {
          this.videoProgressActiveVideos.delete(activeVideo);
          this.updateVideoProgress(activeVideo);
          return;
        }

        this.updateVideoProgress(activeVideo);
      });

      this.videoProgressLoopId = requestAnimationFrame(tick);
    };

    this.videoProgressLoopId = requestAnimationFrame(tick);
  }

  startVideoProgressLoop(video) {
    if (!video || this.videoProgressActiveVideos.has(video)) return;

    this.videoProgressActiveVideos.add(video);
    this.scheduleVideoProgressLoop();
  }

  stopVideoProgressLoop(video) {
    if (!video) return;

    this.videoProgressActiveVideos.delete(video);

    if (!this.videoProgressActiveVideos.size && this.videoProgressLoopId) {
      cancelAnimationFrame(this.videoProgressLoopId);
      this.videoProgressLoopId = null;
    }
  }

  resetVideoProgress(video) {
    this.stopVideoProgressLoop(video);
    this.videoProgressRatios.delete(video);

    const progress = this.getVideoProgressElements(video);
    if (progress?.fill) {
      progress.fill.style.transform = 'scaleX(0)';
    }
    if (progress?.track) {
      progress.track.style.setProperty('--progress', '0');
      progress.track.classList.remove('is-visible');
      progress.track.setAttribute('aria-hidden', 'true');
    }
  }

  setVideoProgressVisible(video, isVisible) {
    const progress = this.getVideoProgressElements(video);
    if (!progress?.track) return;

    progress.track.classList.toggle('is-visible', isVisible);
    progress.track.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  setupVideoProgress() {
    this.inlineVideos.forEach((video) => {
      const onPlaybackChange = () => {
        if (video.paused || video.ended) {
          this.stopVideoProgressLoop(video);
          this.updateVideoProgress(video);
          return;
        }

        this.startVideoProgressLoop(video);
      };
      const onSeek = () => this.updateVideoProgress(video);
      const onEnded = () => this.resetVideoProgress(video);

      video.addEventListener('play', onPlaybackChange);
      video.addEventListener('pause', onPlaybackChange);
      video.addEventListener('loadedmetadata', onSeek);
      video.addEventListener('seeked', onSeek);
      video.addEventListener('ended', onEnded);

      this.setupVideoProgressScrub(video);

      if (!video.paused) {
        this.startVideoProgressLoop(video);
      }

      this.unsubscribers.push(() => {
        this.stopVideoProgressLoop(video);
        video.removeEventListener('play', onPlaybackChange);
        video.removeEventListener('pause', onPlaybackChange);
        video.removeEventListener('loadedmetadata', onSeek);
        video.removeEventListener('seeked', onSeek);
        video.removeEventListener('ended', onEnded);
      });
    });
  }
}

if (!customElements.get('hat-fit-tabs-v2')) {
  customElements.define('hat-fit-tabs-v2', HatFitTabsV2);
}